import React, { useState } from 'react';

import { usePatientIntakeStatusContext } from '../../../hooks';
import { ActionPrompt } from '../../../components';
import { getStepConfig } from '../../../getStepConfig';
import type { BaseStepsBaseProps } from '../BaseSteps.types.ts';
import { IdVerificationModal } from './IdVerificationModal.tsx';
import { getIsPendingIdVerificationStep } from '../../../utils';

interface IdVerificationActionProps extends BaseStepsBaseProps {}

export const IdVerificationAction: React.FC<IdVerificationActionProps> = ({ onEdit }) => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const { baseSteps, isHealthcareProvider, patientPageModalActions } = usePatientIntakeStatusContext();

  const step = baseSteps?.ID_VERIFICATION;

  const isPendingIdVerificationStep = getIsPendingIdVerificationStep(step);

  const handleOnClick = () => {
    setIsModalOpen(true);
  };

  const handleOnEdit = () => {
    /**
     * Should not do anything if setModalData is not available (should never happen)
     */
    if (isPendingIdVerificationStep && !patientPageModalActions?.setModalData) {
      return;
    }

    /**
     * Opens the Verify Identity Modal
     */
    if (isPendingIdVerificationStep) {
      patientPageModalActions?.setModalData({ modalName: 'VerifyIdentity' });

      return;
    }

    onEdit();
  };

  const handleOnClose = () => {
    setIsModalOpen(false);
  };

  if (!step) {
    return null;
  }

  const stepConfig = getStepConfig('ID_VERIFICATION');

  const title = stepConfig.title({
    step,
    isHealthcareProvider,
  });

  const description = stepConfig.statuses[step.currentStatus.status].description({
    step,
    isHealthcareProvider,
  });

  return (
    <>
      <ActionPrompt
        step={step}
        title={title}
        onEdit={handleOnEdit}
        onClick={handleOnClick}
        description={description}
        isViewOnlyMode={isHealthcareProvider && !isPendingIdVerificationStep}
      />

      {isModalOpen && <IdVerificationModal isOpen={isModalOpen} onClose={handleOnClose} step={step} />}
    </>
  );
};
